import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { ownerLabel } from "../lib/ownerDisplay";
import { apiFieldErrors } from "../lib/companyValidation";
import { reportError } from "../lib/errorReporter";

export default function QuotationNew() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { currentOrg, user } = useAuth();
  const isOwnerRole = currentOrg?.role === "owner";

  const [customers, setCustomers] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", reference_number: "", title: "",
    customer_email: "", customer_phone: "",
    value: "", currency: "EUR", valid_until: "", internal_notes: "",
  });
  // Owner selector state: "" = self (default), "unassigned" = unassigned, <uuid> = specific
  const [ownerChoice, setOwnerChoice] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Phase 3B — the automatic number. The suggestion is INFORMATIONAL: it is not
  // reserved by opening this form, and unless the user edits it the database
  // allocates the authoritative number when the quotation is created.
  const [refTouched, setRefTouched] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/quotations/next-reference")
      .then((r) =>
        setForm((s) =>
          s.reference_number ? s : { ...s, reference_number: r.data?.reference_number || "" },
        ),
      )
      .catch((err) => reportError("quotationNew.nextReference", err));
  }, []);

  useEffect(() => {
    api
      .get("/customers")
      .then((r) => setCustomers(r.data || []))
      .catch((err) => reportError("quotationNew.loadCustomers", err));
    if (isOwnerRole) {
      api
        .get("/organization/members/assignable")
        .then((r) => setMembers(r.data || []))
        .catch((err) => reportError("quotationNew.loadMembers", err));
    }
  }, [isOwnerRole]);

  const selfMember = useMemo(
    () => members.find((m) => m.user_id === user?.id) || null,
    [members, user?.id],
  );
  const otherMembers = useMemo(
    () => members.filter((m) => m.user_id !== user?.id),
    [members, user?.id],
  );

  function upd(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
    setFieldErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(""); setFieldErrors({});
    try {
      const payload = {
        ...form,
        value: form.value === "" ? null : Number(form.value),
        valid_until: form.valid_until || null,
        // The backend must not guess: an untouched suggestion is "auto" and the
        // number is allocated atomically in the database.
        reference_mode: refTouched ? "custom" : "auto",
      };
      // Only include owner_user_id when the owner explicitly picked something
      // other than "self". Sales users cannot pick anything else — the field
      // is fixed to their name and the payload omits owner_user_id, letting
      // the backend default to ctx.user_id.
      if (isOwnerRole) {
        if (ownerChoice === "unassigned") {
          payload.owner_user_id = null;
        } else if (ownerChoice && ownerChoice !== "self") {
          payload.owner_user_id = ownerChoice;
        }
      }
      const r = await api.post("/quotations", payload);
      const q = r.data;
      const file = fileRef.current?.files?.[0];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/quotations/${q.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      }
      nav(`/quotations/${q.id}`);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const fields = apiFieldErrors(err);
      if (fields) {
        const duplicate = code === "duplicate_reference";
        const message = duplicate
          ? t("quotations.reference_in_use")
          : fields.reference_number || Object.values(fields)[0] || "";
        setFieldErrors(duplicate ? { ...fields, reference_number: message } : fields);
        setError(message);
      } else {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : String(err?.message || err));
      }
    } finally { setBusy(false); }
  }

  return (
    <Layout title={t("quotations.new")}>
      <div className="h1">{t("quotations.new")}</div>
      <div style={{ height: 16 }} />
      <form className="qw-card" onSubmit={submit} style={{ maxWidth: 720 }}>
        <div className="qw-grid cols-2">
          <div className="qw-field">
            <label>{t("quotations.fields.title")}</label>
            <input className="qw-input" required value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="q-title" />
          </div>
          <div className="qw-field">
            <label>{t("quotations.reference_label")}</label>
            <input
              className="qw-input"
              required
              value={form.reference_number}
              onChange={(e) => {
                setRefTouched(true);
                upd("reference_number", e.target.value);
              }}
              data-testid="q-ref"
              aria-invalid={fieldErrors.reference_number ? "true" : undefined}
            />
            {!refTouched && !fieldErrors.reference_number && (
              <div className="qw-help" data-testid="q-ref-auto-hint">
                {t("quotations.reference_auto_hint")}
              </div>
            )}
            {fieldErrors.reference_number && (
              <div className="qw-error" data-testid="q-ref-error">{fieldErrors.reference_number}</div>
            )}
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.customer")}</label>
            <select className="qw-select" required value={form.customer_id} onChange={(e) => upd("customer_id", e.target.value)} data-testid="q-customer">
              <option value="">—</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.customer_email")}</label>
            <input className="qw-input" type="email" value={form.customer_email} onChange={(e) => upd("customer_email", e.target.value)} data-testid="q-cust-email" />
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.customer_phone")}</label>
            <input className="qw-input" value={form.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} data-testid="q-cust-phone" />
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.value")}</label>
            <input className="qw-input" type="number" min="0" step="0.01" value={form.value} onChange={(e) => upd("value", e.target.value)} data-testid="q-value" />
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.currency")}</label>
            <input className="qw-input" value={form.currency} onChange={(e) => upd("currency", e.target.value.toUpperCase())} maxLength={3} data-testid="q-currency" />
          </div>
          <div className="qw-field">
            <label>{t("quotations.fields.valid_until")}</label>
            <input className="qw-input" type="date" value={form.valid_until} onChange={(e) => upd("valid_until", e.target.value)} data-testid="q-valid-until" />
            {/* Phase 3C.3 — INFORMATIONAL ONLY. The organization's reminder
                defaults are applied server-side, atomically with the create,
                so this form deliberately has no reminder controls to get wrong. */}
            <div className="qw-help" data-testid="q-reminder-hint">
              {t("reminders.new_quotation_hint")}
            </div>
          </div>
          <div className="qw-field" style={{ gridColumn: "1 / -1" }}>
            <label>{t("quotations.owner_field_label")}</label>
            {isOwnerRole ? (
              <select
                className="qw-owner-select"
                value={ownerChoice}
                onChange={(e) => setOwnerChoice(e.target.value)}
                data-testid="q-owner-select"
              >
                <option value="">
                  {selfMember ? ownerLabel(selfMember, t) : t("quotations.owner_sales_default_hint")}
                </option>
                <option value="unassigned">{t("quotations.owner_unassigned")}</option>
                {otherMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {ownerLabel(m, t)} · {m.role === "owner" ? t("settings.role_owner") : t("settings.role_sales")}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input
                  className="qw-input"
                  disabled
                  value={user?.email || t("quotations.owner_sales_default_hint")}
                  data-testid="q-owner-self-readonly"
                />
                <div className="qw-help">{t("quotations.owner_sales_default_hint")}</div>
              </>
            )}
          </div>
        </div>
        <div className="qw-field">
          <label>{t("quotations.fields.internal_notes")}</label>
          <textarea className="qw-textarea" value={form.internal_notes} onChange={(e) => upd("internal_notes", e.target.value)} data-testid="q-notes" />
        </div>
        <div className="qw-field">
          <label>{t("quotations.fields.upload")}</label>
          <input className="qw-input" type="file" accept=".pdf,.docx,.xlsx" ref={fileRef} data-testid="q-file" />
        </div>
        {error && <div className="qw-error" data-testid="q-form-error">{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="qw-btn ghost" onClick={() => nav(-1)}>{t("common.cancel")}</button>
          <button type="submit" className="qw-btn primary" disabled={busy} data-testid="q-submit">{busy ? t("common.loading") : t("quotations.create")}</button>
        </div>
      </form>
    </Layout>
  );
}
